import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { CONTACT_REASONS } from "@/data/site";
import { Seo, Reveal, Overline } from "@/components/site/primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", company: "", phone: "", reason: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.reason) e.reason = "Please select a reason.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      setSuccess(data.id);
      setForm(EMPTY);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-transparent border-b border-hairline/20 focus:border-oraya-accent focus:outline-none py-3 text-ink placeholder:text-subtle/50 transition-colors";

  return (
    <div className="pt-36 pb-32">
      <Seo title="Contact — ORAYA" description="Start a conversation with ORAYA." path="/contact" />
      <div className="oraya-container grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <Overline>Contact</Overline>
          <h1 className="display text-4xl sm:text-5xl font-light tracking-tight text-gradient-mist mt-6">Let's start a conversation.</h1>
          <p className="text-lg text-subtle mt-6 leading-relaxed">
            Tell us about your system, your industry or your ambition. We build intelligent systems for
            complex real-world industries.
          </p>
        </Reveal>

        <div className="md:col-span-6 md:col-start-7">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-heavy rounded-3xl p-10 text-center"
              data-testid="contact-success"
            >
              <div className="w-14 h-14 rounded-full bg-invert mx-auto flex items-center justify-center">
                <Check className="w-7 h-7 text-invert-ink" />
              </div>
              <h2 className="display text-2xl font-medium text-ink mt-6">Thank you.</h2>
              <p className="text-subtle mt-3">Your message has reached ORAYA. We'll be in touch shortly.</p>
              <p className="text-subtle/50 text-sm mt-4">Reference: {success}</p>
              <button onClick={() => setSuccess(null)} data-testid="contact-new-btn" className="mt-8 text-sm text-ink underline underline-offset-4">Send another message</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="glass-heavy rounded-3xl p-8 md:p-10 flex flex-col gap-6" data-testid="contact-form" noValidate>
              <div>
                <input className={field} placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" aria-label="Name" />
                {errors.name && <p className="text-xs text-red-400 mt-1" data-testid="error-name">{errors.name}</p>}
              </div>
              <div>
                <input className={field} type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" aria-label="Email" />
                {errors.email && <p className="text-xs text-red-400 mt-1" data-testid="error-email">{errors.email}</p>}
              </div>
              <input className={field} placeholder="Company" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="contact-company" aria-label="Company" />
              <input className={field} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" aria-label="Phone" />
              <div>
                <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
                  <SelectTrigger className="w-full bg-transparent border-0 border-b border-hairline/20 rounded-none px-0 py-3 text-ink focus:ring-0 data-[placeholder]:text-subtle/50" data-testid="contact-reason">
                    <SelectValue placeholder="Reason for contact" />
                  </SelectTrigger>
                  <SelectContent className="bg-panel border-hairline/10 text-ink">
                    {CONTACT_REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`reason-${r}`}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.reason && <p className="text-xs text-red-400 mt-1" data-testid="error-reason">{errors.reason}</p>}
              </div>
              <div>
                <textarea className={`${field} resize-none`} rows={4} placeholder="Message" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" aria-label="Message" />
                {errors.message && <p className="text-xs text-red-400 mt-1" data-testid="error-message">{errors.message}</p>}
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-2 inline-flex items-center justify-center rounded-full px-6 py-3.5 bg-invert text-invert-ink font-medium hover:opacity-90 transition-colors duration-300 disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Start a conversation"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
